import type { EventListItem, Platform } from '@/types/market'
import { create } from 'zustand'

const LEGACY_FAVORITES_KEY = 'predicity_favorites'

type FavoriteEvent = {
	id: string
	platform: Platform
	addedAt: number
}

type FavoriteInput = Pick<EventListItem, 'id' | 'platform'>

type FavoritesStore = {
	favorites: FavoriteEvent[]
	isLoaded: boolean
	loadFavorites: (walletAddress?: string) => void
	toggleFavorite: (event: FavoriteInput, walletAddress?: string) => void
	removeFavorite: (id: string, platform: Platform, walletAddress?: string) => void
	cleanupUnavailable: (
		unavailableIds: Array<{ id: string; platform: Platform }>,
		walletAddress?: string
	) => void
	isFavorite: (id: string, platform: Platform) => boolean
}

// Helper to get wallet-scoped storage key
const getStorageKey = (walletAddress?: string): string | null => {
	if (!walletAddress) {
		return null
	}
	return `predicity_favorites_${walletAddress.toLowerCase()}`
}

// Helper to migrate legacy favorites to wallet-scoped storage
const migrateLegacyFavorites = (walletAddress: string): FavoriteEvent[] => {
	try {
		const storageKey = getStorageKey(walletAddress)
		if (!storageKey) {
			return []
		}

		// Check if wallet already has favorites
		const existing = localStorage.getItem(storageKey)
		if (existing) {
			// Already migrated or has favorites
			return JSON.parse(existing)
		}

		// Check for legacy favorites
		const legacy = localStorage.getItem(LEGACY_FAVORITES_KEY)
		if (!legacy) {
			return []
		}

		const parsed = JSON.parse(legacy) as Record<string, unknown>[]
		const cleaned: FavoriteEvent[] = parsed
			.filter(fav => fav.id && fav.platform)
			.map(fav => ({
				id: String(fav.id),
				platform: fav.platform as Platform,
				addedAt: typeof fav.addedAt === 'number' ? fav.addedAt : Date.now(),
			}))

		// Save to wallet-scoped key
		localStorage.setItem(storageKey, JSON.stringify(cleaned))

		// Clean up legacy key
		localStorage.removeItem(LEGACY_FAVORITES_KEY)

		return cleaned
	} catch (error) {
		console.error('Failed to migrate legacy favorites:', error)
		return []
	}
}

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
	favorites: [],
	isLoaded: false,

	loadFavorites: (walletAddress?: string) => {
		// If no wallet address, clear favorites and mark as loaded
		if (!walletAddress) {
			set({ favorites: [], isLoaded: true })
			return
		}

		try {
			const storageKey = getStorageKey(walletAddress)
			if (!storageKey) {
				set({ favorites: [], isLoaded: true })
				return
			}

			// Try to migrate legacy favorites first
			const migrated = migrateLegacyFavorites(walletAddress)
			if (migrated.length > 0) {
				set({ favorites: migrated, isLoaded: true })
				return
			}

			// Load from wallet-scoped storage
			const stored = localStorage.getItem(storageKey)
			if (stored) {
				const parsed = JSON.parse(stored) as Record<string, unknown>[]
				const cleaned: FavoriteEvent[] = parsed
					.filter(fav => fav.id && fav.platform)
					.map(fav => ({
						id: String(fav.id),
						platform: fav.platform as Platform,
						addedAt: typeof fav.addedAt === 'number' ? fav.addedAt : Date.now(),
					}))
				set({ favorites: cleaned, isLoaded: true })
			} else {
				set({ favorites: [], isLoaded: true })
			}
		} catch (error) {
			console.error('Failed to load favorites:', error)
			set({ favorites: [], isLoaded: true })
		}
	},

	toggleFavorite: (event: FavoriteInput, walletAddress?: string) => {
		const storageKey = getStorageKey(walletAddress)
		if (!storageKey) {
			console.warn('Cannot toggle favorite: no wallet address provided')
			return
		}

		const { favorites } = get()
		const isFavorite = favorites.some(f => f.id === event.id && f.platform === event.platform)

		let newFavorites: FavoriteEvent[]
		if (isFavorite) {
			newFavorites = favorites.filter(f => !(f.id === event.id && f.platform === event.platform))
		} else {
			const favorite: FavoriteEvent = {
				id: event.id,
				platform: event.platform,
				addedAt: Date.now(),
			}
			newFavorites = [favorite, ...favorites]
		}

		try {
			localStorage.setItem(storageKey, JSON.stringify(newFavorites))
			set({ favorites: newFavorites })
		} catch (error) {
			console.error('Failed to save favorites:', error)
		}
	},

	removeFavorite: (id: string, platform: Platform, walletAddress?: string) => {
		const storageKey = getStorageKey(walletAddress)
		if (!storageKey) {
			console.warn('Cannot remove favorite: no wallet address provided')
			return
		}

		const { favorites } = get()
		const newFavorites = favorites.filter(f => !(f.id === id && f.platform === platform))

		try {
			localStorage.setItem(storageKey, JSON.stringify(newFavorites))
			set({ favorites: newFavorites })
		} catch (error) {
			console.error('Failed to remove favorite:', error)
		}
	},

	cleanupUnavailable: (
		unavailableIds: Array<{ id: string; platform: Platform }>,
		walletAddress?: string
	) => {
		const storageKey = getStorageKey(walletAddress)
		if (!storageKey) {
			console.warn('Cannot cleanup favorites: no wallet address provided')
			return
		}

		const { favorites } = get()
		const unavailableSet = new Set(unavailableIds.map(u => `${u.platform}:${u.id}`))
		const newFavorites = favorites.filter(f => !unavailableSet.has(`${f.platform}:${f.id}`))

		if (newFavorites.length !== favorites.length) {
			try {
				localStorage.setItem(storageKey, JSON.stringify(newFavorites))
				set({ favorites: newFavorites })
			} catch (error) {
				console.error('Failed to cleanup favorites:', error)
			}
		}
	},

	isFavorite: (id: string, platform: Platform) => {
		const { favorites } = get()
		return favorites.some(f => f.id === id && f.platform === platform)
	},
}))
